import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SplashScreenProps {
  onComplete: () => void;
}

const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onComplete, 500);
    }, 3500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-gradient-to-br from-cream via-cream-dark to-cream flex flex-col items-center justify-center overflow-hidden"
        >
          {/* Animated Particles */}
          <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  opacity: 0,
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                }}
                animate={{
                  opacity: [0, 0.4, 0],
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
                className="absolute w-2 h-2 bg-gold rounded-full"
              />
            ))}
          </div>

          {/* Building Line Animation */}
          <svg
            width="300"
            height="300"
            viewBox="0 0 300 300"
            className="relative z-10"
          >
            {/* Building Outline */}
            <motion.path
              d="M 50 250 L 50 100 L 100 80 L 150 100 L 150 250 M 100 100 L 100 250 M 50 130 L 150 130 M 50 160 L 150 160 M 50 190 L 150 190 M 50 220 L 150 220"
              stroke="#2D4A3E"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
            
            {/* Additional Building Details */}
            <motion.path
              d="M 170 250 L 170 140 L 250 140 L 250 250 M 210 140 L 210 250 M 170 170 L 250 170 M 170 200 L 250 200 M 170 230 L 250 230"
              stroke="#2D4A3E"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 2, delay: 0.5, ease: 'easeInOut' }}
            />

            {/* Glow Effect */}
            <motion.circle
              cx="150"
              cy="150"
              r="80"
              fill="none"
              stroke="#C9A962"
              strokeWidth="1"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 2, opacity: [0, 0.3, 0] }}
              transition={{ duration: 2, delay: 1 }}
            />
          </svg>

          {/* Work Mauriti Text */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="relative z-10 mt-6 text-4xl md:text-5xl font-serif font-bold text-forest"
          >
            Work Mauriti
          </motion.h1>

          {/* Loading Text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, delay: 2, repeat: Infinity }}
            className="absolute bottom-20 text-forest text-sm font-sans tracking-widest"
          >
            SEU ESPAÇO DE NEGÓCIOS
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
